import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { imageFilesFromDataTransfer, isFileDrag } from './images.ts';

export type ImageDropHandlers = {
  onDragEnter: (e: DragEvent) => void;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
};

/**
 * Files cannot ride `history.pushState`, so a drop on the home list stashes
 * them here for the chat composer to pick up after navigation.
 */
let pending: { sessionId: string; tab: 'chat' | 'task'; files: File[] } | null = null;

export function stashDroppedImages(
  sessionId: string,
  tab: 'chat' | 'task',
  files: File[],
): void {
  pending = { sessionId, tab, files };
}

export function takeDroppedImages(sessionId: string, tab: 'chat' | 'task'): File[] {
  if (!pending || pending.sessionId !== sessionId || pending.tab !== tab) return [];
  const files = pending.files;
  pending = null;
  return files;
}

export function useImageDrop(
  onFiles: (files: File[]) => void,
  enabled = true,
): { over: boolean; handlers: ImageDropHandlers } {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;

  const reset = (): void => {
    depth.current = 0;
    setOver(false);
  };

  useEffect(() => {
    if (!enabled) reset();
  }, [enabled]);

  const onDragEnter = useCallback(
    (e: DragEvent) => {
      if (!enabled || !isFileDrag(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      depth.current += 1;
      setOver(true);
    },
    [enabled],
  );

  const onDragOver = useCallback(
    (e: DragEvent) => {
      if (!enabled || !isFileDrag(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
    },
    [enabled],
  );

  const onDragLeave = useCallback(
    (e: DragEvent) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setOver(false);
    },
    [enabled],
  );

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();
      reset();
      const files = imageFilesFromDataTransfer(e.dataTransfer);
      if (files.length > 0) onFilesRef.current(files);
    },
    [enabled],
  );

  return { over, handlers: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}
